import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Simple Morocco Journey Game with location-based travel
function MoroccoJourneySimple({ userToken }) {
  const [gameConfig, setGameConfig] = useState(null);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [wallet, setWallet] = useState(1000);
  const [gems, setGems] = useState(0);
  const [showTravelMenu, setShowTravelMenu] = useState(false);
  const [selectedDestination, setSelectedDestination] = useState(null);
  const [isTraveling, setIsTraveling] = useState(false);
  const [travelProgress, setTravelProgress] = useState(0);
  const [showCharacter, setShowCharacter] = useState(false);
  const [characterDialogue, setCharacterDialogue] = useState('');
  const [showStore, setShowStore] = useState(false);
  const [showHotel, setShowHotel] = useState(false);

  // Load game config
  useEffect(() => {
    loadGameConfig();
  }, []);

  const loadGameConfig = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/game/config`);
      setGameConfig(response.data);
      
      // Set current location to airport by default
      const airport = response.data.locations.find(loc => loc.id === 'airport');
      if (airport) {
        setCurrentLocation(airport);
      }
    } catch (error) {
      console.error('Failed to load game config:', error);
    }
  };

  // Start travel to destination
  const startTravel = (destination) => {
    const taxiCost = gameConfig.taxi_cost || 200;
    
    if (wallet < taxiCost) {
      alert('Not enough money! You need ' + taxiCost + ' MAD');
      return;
    }
    
    setSelectedDestination(destination);
    setIsTraveling(true);
    setTravelProgress(0);
    
    // Simulate travel with progress
    let progress = 0;
    const interval = setInterval(() => {
      progress += 10;
      setTravelProgress(progress);
      
      if (progress >= 100) {
        clearInterval(interval);
        completeTravel(destination, taxiCost);
      }
    }, 500);
  };

  // Complete travel
  const completeTravel = (destination, cost) => {
    setWallet(wallet - cost);
    setCurrentLocation(destination);
    setIsTraveling(false);
    setShowTravelMenu(false);
    setSelectedDestination(null);
    
    // Show character dialogue at new location
    const character = gameConfig.characters.find(char => char.location_id === destination.id);
    if (character) {
      setCharacterDialogue(character.dialogue_phrases[0]);
      setShowCharacter(true);
    }
  };

  // Close character dialogue
  const closeCharacterDialogue = () => {
    setShowCharacter(false);
  };

  // Open store
  const openStore = () => {
    setShowStore(true);
  };

  // Open hotel
  const openHotel = () => {
    setShowHotel(true);
  };

  // Buy item from store
  const buyItem = (item) => {
    if (wallet >= item.price) {
      setWallet(wallet - item.price);
      alert(`Bought ${item.name} for ${item.price} MAD!`);
    } else {
      alert('Not enough money!');
    }
  };

  // Book hotel room
  const bookRoom = (room) => {
    if (wallet >= room.price) {
      setWallet(wallet - room.price);
      alert(`Booked ${room.type} for ${room.price} MAD!`);
      setShowHotel(false);
    } else {
      alert('Not enough money!');
    }
  };

  if (!gameConfig || !currentLocation) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.loadingSpinner}></div>
        <p style={styles.loadingText}>Loading Morocco Journey...</p>
      </div>
    );
  }

  // Get available destinations (all locations except current)
  const availableDestinations = gameConfig.locations.filter(loc => loc.id !== currentLocation.id);

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.title}>🇲🇦 Morocco Journey</h1>
        <div style={styles.stats}>
          <div style={styles.stat}>💰 {wallet} MAD</div>
          <div style={styles.stat}>💎 {gems} Gems</div>
        </div>
      </div>

      {/* Traveling Animation */}
      {isTraveling && selectedDestination && (
        <div style={styles.travelOverlay}>
          <div style={styles.travelBox}>
            <h2 style={styles.travelTitle}>🚕 Traveling...</h2>
            <p style={styles.travelText}>
              From: <strong>{currentLocation.name}</strong>
            </p>
            <p style={styles.travelText}>
              To: <strong>{selectedDestination.name}</strong>
            </p>
            <div style={styles.progressBar}>
              <div style={{...styles.progressFill, width: `${travelProgress}%`}}></div>
            </div>
            <p style={styles.travelProgress}>{travelProgress}%</p>
          </div>
        </div>
      )}

      {/* Character Dialogue */}
      {showCharacter && (
        <div style={styles.dialogueOverlay}>
          <div style={styles.dialogueBox}>
            <div style={styles.dialogueHeader}>
              <h2 style={styles.dialogueTitle}>👤 Character</h2>
              <button onClick={closeCharacterDialogue} style={styles.closeButton}>✕</button>
            </div>
            <p style={styles.dialogueText}>{characterDialogue}</p>
            <button onClick={closeCharacterDialogue} style={styles.dialogueButton}>
              Continue
            </button>
          </div>
        </div>
      )}

      {/* Store Modal */}
      {showStore && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalBox}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>🛍️ Market Store</h2>
              <button onClick={() => setShowStore(false)} style={styles.closeButton}>✕</button>
            </div>
            <div style={styles.storeGrid}>
              {gameConfig.store_items.map((item, idx) => (
                <div key={idx} style={styles.storeItem}>
                  <div style={styles.itemEmoji}>{item.emoji || '📦'}</div>
                  <div style={styles.itemInfo}>
                    <strong style={styles.itemName}>{item.name}</strong>
                    <p style={styles.itemDesc}>{item.description}</p>
                    <p style={styles.itemPrice}>💰 {item.price} MAD</p>
                  </div>
                  <button 
                    onClick={() => buyItem(item)}
                    style={styles.buyButton}
                  >
                    Buy
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Hotel Modal */}
      {showHotel && currentLocation.hotel_data && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalBox}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>🏨 Hotel</h2>
              <button onClick={() => setShowHotel(false)} style={styles.closeButton}>✕</button>
            </div>
            <div style={styles.roomGrid}>
              {currentLocation.hotel_data.rooms.map((room, idx) => (
                <div key={idx} style={styles.roomItem}>
                  <h3 style={styles.roomName}>{room.type}</h3>
                  <p style={styles.roomPrice}>💰 {room.price} MAD/night</p>
                  <p style={styles.roomAvailable}>Available: {room.available}</p>
                  <button 
                    onClick={() => bookRoom(room)}
                    style={styles.bookButton}
                  >
                    Book Room
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Main Game Area */}
      <div style={styles.gameArea}>
        {/* Current Location Display */}
        <div style={styles.locationDisplay}>
          <div style={styles.locationIcon}>
            {getLocationIcon(currentLocation.location_type)}
          </div>
          <div style={styles.locationInfo}>
            <h2 style={styles.locationName}>{currentLocation.name}</h2>
            <p style={styles.locationDesc}>{currentLocation.description}</p>
            <p style={styles.locationType}>Type: {currentLocation.location_type}</p>
          </div>
        </div>

        {/* Play Button */}
        <button 
          onClick={() => setShowTravelMenu(true)}
          style={styles.playButton}
        >
          ▶️ PLAY - Travel to Another Location
        </button>

        {/* Location Actions */}
        <div style={styles.actionButtons}>
          {currentLocation.location_type === 'shopping' && (
            <button onClick={openStore} style={styles.actionButton}>
              🛍️ Open Store
            </button>
          )}
          {currentLocation.location_type === 'hotel' && (
            <button onClick={openHotel} style={styles.actionButton}>
              🏨 Book Room
            </button>
          )}
          {currentLocation.location_type === 'food' && (
            <button style={styles.actionButton}>
              🍽️ Order Food
            </button>
          )}
        </div>

        {/* Travel Menu */}
        {showTravelMenu && (
          <div style={styles.travelMenu}>
            <div style={styles.travelMenuHeader}>
              <h2 style={styles.travelMenuTitle}>🗺️ Choose Destination</h2>
              <button onClick={() => setShowTravelMenu(false)} style={styles.closeButton}>✕</button>
            </div>
            <div style={styles.destinationGrid}>
              {availableDestinations.map((dest, idx) => (
                <div 
                  key={idx}
                  onClick={() => startTravel(dest)}
                  style={styles.destinationCard}
                >
                  <div style={styles.destinationIcon}>
                    {getLocationIcon(dest.location_type)}
                  </div>
                  <div style={styles.destinationInfo}>
                    <h3 style={styles.destinationName}>{dest.name}</h3>
                    <p style={styles.destinationDesc}>{dest.description}</p>
                    <p style={styles.taxiCost}>🚕 Taxi: {gameConfig.taxi_cost} MAD</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Helper function to get location icon
const getLocationIcon = (type) => {
  const icons = {
    transport: '✈️',
    hotel: '🏨',
    shopping: '🛍️',
    food: '🍽️',
    activity: '🏋️',
    scenic: '🌊',
    cultural: '🕌',
  };
  return icons[type] || '📍';
};

const styles = {
  container: {
    width: '100%',
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    fontFamily: 'Arial, sans-serif',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    color: 'white',
  },
  loadingSpinner: {
    width: '50px',
    height: '50px',
    border: '5px solid rgba(255,255,255,0.3)',
    borderTop: '5px solid white',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  loadingText: {
    marginTop: '20px',
    fontSize: '18px',
  },
  header: {
    background: 'rgba(0,0,0,0.8)',
    padding: '20px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  title: {
    color: '#FFD700',
    fontSize: '24px',
    margin: 0,
  },
  stats: {
    display: 'flex',
    gap: '20px',
  },
  stat: {
    background: 'rgba(255,255,255,0.2)',
    padding: '10px 20px',
    borderRadius: '20px',
    color: '#FFD700',
    fontSize: '16px',
    fontWeight: 'bold',
  },
  gameArea: {
    padding: '40px',
    maxWidth: '1200px',
    margin: '0 auto',
  },
  locationDisplay: {
    background: 'white',
    borderRadius: '20px',
    padding: '30px',
    marginBottom: '30px',
    display: 'flex',
    gap: '20px',
    alignItems: 'center',
    boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
  },
  locationIcon: {
    fontSize: '80px',
    minWidth: '100px',
    textAlign: 'center',
  },
  locationInfo: {
    flex: 1,
  },
  locationName: {
    color: '#D32F2F',
    fontSize: '28px',
    margin: '0 0 10px 0',
  },
  locationDesc: {
    color: '#666',
    fontSize: '16px',
    margin: '0 0 10px 0',
  },
  locationType: {
    color: '#999',
    fontSize: '14px',
    margin: 0,
  },
  playButton: {
    width: '100%',
    padding: '25px',
    fontSize: '24px',
    fontWeight: 'bold',
    background: 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '15px',
    cursor: 'pointer',
    boxShadow: '0 6px 20px rgba(76, 175, 80, 0.4)',
    transition: 'transform 0.2s',
  },
  playButtonHover: {
    transform: 'scale(1.02)',
  },
  actionButtons: {
    display: 'flex',
    gap: '15px',
    marginTop: '20px',
    flexWrap: 'wrap',
  },
  actionButton: {
    flex: 1,
    padding: '15px 25px',
    fontSize: '16px',
    fontWeight: 'bold',
    background: 'linear-gradient(135deg, #2196F3 0%, #1976D2 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '12px',
    cursor: 'pointer',
    boxShadow: '0 4px 15px rgba(33, 150, 243, 0.3)',
    minWidth: '200px',
  },
  travelMenu: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.9)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  travelMenuHeader: {
    width: '90%',
    maxWidth: '1000px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
  },
  travelMenuTitle: {
    color: '#FFD700',
    fontSize: '28px',
    margin: 0,
  },
  destinationGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: '20px',
    width: '90%',
    maxWidth: '1000px',
    maxHeight: '70vh',
    overflowY: 'auto',
  },
  destinationCard: {
    background: 'white',
    borderRadius: '15px',
    padding: '20px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    border: '3px solid transparent',
  },
  destinationCardHover: {
    borderColor: '#FFD700',
    transform: 'scale(1.02)',
  },
  destinationIcon: {
    fontSize: '60px',
    marginBottom: '10px',
  },
  destinationInfo: {
    textAlign: 'center',
  },
  destinationName: {
    color: '#D32F2F',
    fontSize: '20px',
    margin: '0 0 10px 0',
  },
  destinationDesc: {
    color: '#666',
    fontSize: '14px',
    margin: '0 0 10px 0',
  },
  taxiCost: {
    color: '#FF6B6B',
    fontSize: '16px',
    fontWeight: 'bold',
    margin: 0,
  },
  travelOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.9)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 2000,
  },
  travelBox: {
    background: 'white',
    borderRadius: '20px',
    padding: '50px',
    textAlign: 'center',
    boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
    maxWidth: '500px',
  },
  travelTitle: {
    color: '#D32F2F',
    fontSize: '32px',
    margin: '0 0 20px 0',
  },
  travelText: {
    color: '#333',
    fontSize: '18px',
    margin: '10px 0',
  },
  progressBar: {
    width: '100%',
    height: '30px',
    background: '#E0E0E0',
    borderRadius: '15px',
    overflow: 'hidden',
    margin: '20px 0',
  },
  progressFill: {
    height: '100%',
    background: 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)',
    transition: 'width 0.5s ease',
  },
  travelProgress: {
    color: '#4CAF50',
    fontSize: '24px',
    fontWeight: 'bold',
    margin: '10px 0 0 0',
  },
  dialogueOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.8)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1500,
  },
  dialogueBox: {
    background: 'white',
    borderRadius: '20px',
    padding: '40px',
    maxWidth: '600px',
    boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
  },
  dialogueHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
  },
  dialogueTitle: {
    color: '#D32F2F',
    fontSize: '24px',
    margin: 0,
  },
  dialogueText: {
    color: '#333',
    fontSize: '18px',
    lineHeight: '1.6',
    marginBottom: '20px',
  },
  dialogueButton: {
    width: '100%',
    padding: '15px',
    fontSize: '18px',
    fontWeight: 'bold',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '12px',
    cursor: 'pointer',
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.9)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1500,
  },
  modalBox: {
    background: 'white',
    borderRadius: '20px',
    padding: '40px',
    maxWidth: '900px',
    maxHeight: '80vh',
    overflowY: 'auto',
    boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
  },
  modalTitle: {
    color: '#D32F2F',
    fontSize: '28px',
    margin: 0,
  },
  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: '32px',
    cursor: 'pointer',
    color: '#666',
  },
  storeGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '20px',
  },
  storeItem: {
    border: '2px solid #E0E0E0',
    borderRadius: '12px',
    padding: '20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  itemEmoji: {
    fontSize: '60px',
    textAlign: 'center',
  },
  itemInfo: {
    textAlign: 'center',
  },
  itemName: {
    color: '#333',
    fontSize: '18px',
    margin: '0 0 5px 0',
  },
  itemDesc: {
    color: '#666',
    fontSize: '14px',
    margin: '0 0 10px 0',
  },
  itemPrice: {
    color: '#FF6B6B',
    fontSize: '18px',
    fontWeight: 'bold',
    margin: '0 0 10px 0',
  },
  buyButton: {
    padding: '12px 20px',
    fontSize: '16px',
    fontWeight: 'bold',
    background: 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
    width: '100%',
  },
  roomGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '20px',
  },
  roomItem: {
    border: '2px solid #E0E0E0',
    borderRadius: '12px',
    padding: '25px',
    textAlign: 'center',
  },
  roomName: {
    color: '#333',
    fontSize: '20px',
    margin: '0 0 10px 0',
  },
  roomPrice: {
    color: '#FF6B6B',
    fontSize: '18px',
    fontWeight: 'bold',
    margin: '0 0 10px 0',
  },
  roomAvailable: {
    color: '#666',
    fontSize: '16px',
    margin: '0 0 15px 0',
  },
  bookButton: {
    padding: '12px 20px',
    fontSize: '16px',
    fontWeight: 'bold',
    background: 'linear-gradient(135deg, #2196F3 0%, #1976D2 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
  },
};

export default MoroccoJourneySimple;
